import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { getIndustryNameById, fetchHHMetroCities, fetchHHCountries, type MetroCityOption, type CountryOption } from '../services/hhDictionariesService';
import { SearchableMetroSelect } from '../components/ui/searchable-metro-select';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { XMarkIcon as XMarkIconSolid } from '@heroicons/react/24/solid';
import { ArrowDownSLineIconV7 } from '../components/ui/figma-icons-v7';
import SkillsInput from './SkillsInput';
import TagsInput from './TagsInput';
import DonorCompaniesAutocomplete from './components/DonorCompaniesAutocomplete';
import { extractLastPathSegment, getExperienceOptions, EMPLOYMENT_TYPE_OPTIONS, HH_EXPERIENCE_OPTIONS, HH_POSITION_EXPERIENCE_OPTIONS, HH_EXPERIENCE_PERIOD_OPTIONS } from '../utils/filterUtils';
import { parseSkillGroupsRaw } from '../utils/skillGroupsParse';
import { useBrandingColors } from '../hooks/useBrandingColors';
import TextInput from '../components/ui/text-input';
import { FiltersState, defaultFiltersState } from '../hooks/useFilters';
import { useCurrencies } from '../hooks/useCurrencies';
import { getSourceConfig } from '../sources';
import { getDefaultFiltersForSource } from '../sources/filterPolicy';
import CustomCheckbox from '../components/ui/CustomCheckbox';
import { formatNumberWithSpaces, parseFormattedNumber } from '../utils/numberFormat';
import { FilterModal } from '../components/search/FilterModal';
import SpecializationModal from '../components/ui/SpecializationModal';
import {
  getSpecializationDisplayCacheKey,
  getSpecializationDisplayName,
} from '../sources/specializationDisplay';

// Опции для выпадающих списков
const INDUSTRY_OPTIONS = [
  { value: '', label: 'Выберите отрасль...' },
  { value: 'it', label: 'IT' },
  { value: 'finance', label: 'Финансы' },
  { value: 'marketing', label: 'Маркетинг' },
  { value: 'sales', label: 'Продажи' },
  { value: 'hr', label: 'HR' },
  { value: 'education', label: 'Образование' },
  { value: 'healthcare', label: 'Здравоохранение' },
  { value: 'manufacturing', label: 'Производство' },
  { value: 'retail', label: 'Розничная торговля' },
];

const SPECIALIZATION_OPTIONS = [
  { value: '', label: 'Выберите специализацию...' },
  { value: 'frontend', label: 'Frontend разработка' },
  { value: 'backend', label: 'Backend разработка' },
  { value: 'fullstack', label: 'Fullstack разработка' },
  { value: 'mobile', label: 'Мобильная разработка' },
  { value: 'devops', label: 'DevOps' },
  { value: 'data', label: 'Data Science' },
  { value: 'qa', label: 'QA/Тестирование' },
  { value: 'design', label: 'UI/UX дизайн' },
  { value: 'product', label: 'Product Management' },
];

const EXPERIENCE_OPTIONS = [
  { value: '', label: 'Выберите опыт...' },
  { value: 'no-experience', label: 'Без опыта' },
  { value: '1-3', label: '1-3 года' },
  { value: '3-5', label: '3-5 лет' },
  { value: '5-10', label: '5-10 лет' },
  { value: '10+', label: 'Более 10 лет' },
];

const SCHEDULE_OPTIONS = [
  { value: '', label: 'Выберите график...' },
  { value: 'full-time', label: 'Полная занятость' },
  { value: 'part-time', label: 'Частичная занятость' },
  { value: 'remote', label: 'Удаленная работа' },
  { value: 'hybrid', label: 'Гибридная работа' },
  { value: 'flexible', label: 'Гибкий график' },
];

const EDUCATION_OPTIONS = [
  { value: 'secondary', label: 'Среднее' },
  { value: 'special_secondary', label: 'Среднее специальное' },
  { value: 'unfinished_higher', label: 'Неоконченное высшее' },
  { value: 'higher', label: 'Высшее' },
  { value: 'bachelor', label: 'Бакалавр' },
  { value: 'master', label: 'Магистр' },
  { value: 'candidate', label: 'Кандидат наук' },
  { value: 'doctor', label: 'Доктор наук' },
];

const JOB_STATUS_OPTIONS = [
  { value: '', label: 'Выберите статус...' },
  { value: 'employed', label: 'Работает' },
  { value: 'unemployed', label: 'Не работает' },
  { value: 'looking', label: 'Ищет работу' },
  { value: 'not-looking', label: 'Не ищет работу' },
];

const AVITO_EDUCATION_OPTIONS = [
  { value: 'higher', label: 'Высшее' },
  { value: 'unfinished-higher', label: 'Неоконченное высшее' },
  { value: 'secondary', label: 'Среднее' },
  { value: 'special-secondary', label: 'Среднее специальное' },
];

const AVITO_SCHEDULE_OPTIONS = [
  { value: 'full-day', label: 'Полный рабочий день' },
  { value: 'partial-day', label: 'Неполный рабочий день' },
  { value: 'remote', label: 'Удалённая работа' },
  { value: 'flexible', label: 'Гибкий график' },
  { value: 'shift', label: 'Сменный график' },
  { value: 'fly-in-fly-out', label: 'Вахтовый метод' },
];

const AVITO_BUSINESS_TRIP_OPTIONS = [
  { value: 'ready', label: 'Готов' },
  { value: 'sometimes', label: 'Иногда' },
  { value: 'never', label: 'Не готов' },
];

const AVITO_RELOCATION_OPTIONS = [
  { value: 'possible', label: 'Возможен' },
  { value: 'never', label: 'Невозможен' },
];

const AVITO_DRIVING_EXPERIENCE_OPTIONS = [
  { value: 'less-than-three-years', label: 'Меньше трёх лет' },
  { value: 'more-than-three-years', label: 'Больше трёх лет' },
];

const AVITO_OWN_TRANSPORT_OPTIONS = [
  { value: 'false', label: 'Нет' },
  { value: 'car', label: 'Легковое авто' },
  { value: 'cargo-car', label: 'Грузовое авто' },
  { value: 'bike', label: 'Мотоцикл' },
  { value: 'scooter', label: 'Мопед' },
];

const AVITO_DRIVER_CATEGORIES = ['a', 'b', 'be', 'c', 'ce', 'd', 'de', 'm', 'tm', 'tb'];

// Кастомный компонент выпадающего списка в стиле Figma
const CustomSelect: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
}> = ({ label, value, onChange, options, placeholder = 'Выберите...' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Закрытие при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (newValue: string) => {
    onChange(newValue);
    setIsOpen(false);
  };

  const displayValue = options.find(opt => opt.value === value)?.label || placeholder;

  return (
    <div className="content-stretch flex flex-col gap-[4px] items-center relative shrink-0 w-full" ref={dropdownRef}>
      <div className="content-stretch flex gap-px items-center relative shrink-0 w-full">
        <div className="flex flex-col font-['Inter:Regular',_sans-serif] font-normal justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black text-nowrap tracking-[-0.084px]">
          <p className="leading-[20px] whitespace-pre">{label}</p>
        </div>
      </div>
      <div className="bg-white relative rounded-[10px] shrink-0 w-full">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="box-border content-stretch flex gap-[8px] items-center overflow-clip pl-[12px] pr-[10px] py-[10px] relative w-full hover:bg-[#f6f8fa] transition-colors cursor-pointer"
        >
          <div className={`basis-0 font-['Inter:Regular',_sans-serif] font-normal grow leading-[0] min-h-px min-w-px not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-[14px] text-left tracking-[-0.084px] ${value ? 'text-[#525866]' : 'text-[#868c98]'}`}>
            <p className="[white-space-collapse:collapse] leading-[20px] overflow-ellipsis overflow-hidden">
              {displayValue}
            </p>
          </div>
          <div className="overflow-clip relative shrink-0 size-[20px]">
            <div className={`absolute inset-[10%_5%_10%_5%] transition-transform ${isOpen ? 'rotate-180' : ''}`}>
              <svg className="block max-w-none size-full" fill="none" viewBox="0 0 20 20">
                <path d="M10.5001 12.4144L15.2072 7.70733L16.2678 8.76794L10.5001 14.5356L4.73242 8.76794L5.79303 7.70733L10.5001 12.4144Z" fill="#525866"/>
              </svg>
            </div>
          </div>
        </button>
        <div aria-hidden="true" className="absolute border border-[#e2e4e9] border-solid inset-0 pointer-events-none rounded-[10px] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)]" />

        {isOpen && (
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
            <div className="absolute left-0 right-0 bg-[#ffffff] rounded-[10px] border border-[#e2e4e9] shadow-[0px_4px_12px_0px_rgba(0,0,0,0.08)] z-[9999] p-2 max-h-48 overflow-y-auto text-[14px] top-full mt-2">
              {options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={`w-full text-left px-3 py-2 rounded-[8px] hover:bg-[#f6f8fa] transition-colors whitespace-nowrap ${
                    value === option.value ? 'text-[#e16349]' : 'text-[#525866]'
                  }`}
                  style={value === option.value ? { backgroundColor: 'rgba(225, 99, 73, 0.1)' } : undefined}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Hoisted AccordionSection to preserve component identity across renders
const AccordionSection: React.FC<{
  id: string;
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ id, title, isOpen, onToggle, children }) => {
  return (
    <div>
      <button
        onClick={onToggle}
        className="bg-[#ffffff] border relative rounded-[24px] shrink-0 w-full cursor-pointer transition-all hover:bg-[#f6f8fa]"
        style={{ borderColor: 'var(--brand-primary)' }}
        aria-expanded={isOpen}
        aria-controls={`section-${id}`}
      >
        <div className="flex flex-row items-center overflow-clip relative size-full">
          <div className="box-border content-stretch flex flex-row gap-2 items-center justify-start pl-2.5 pr-2 py-1.5 relative w-full">
            <div className="basis-0 css-bbeeyw font-['Inter:Regular',_sans-serif] font-normal grow leading-[0] min-h-px min-w-px not-italic relative shrink-0 text-[#525866] text-[16px] text-left tracking-[-0.176px]">
              <p className="block leading-[24px]">
                {title}
              </p>
            </div>
            <div className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} style={{ width: 28, height: 28 }}>
              <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 20 20">
                <g id="arrow-down-s-line">
                  <path d="M10.5001 12.4144L15.2072 7.70733L16.2678 8.76794L10.5001 14.5356L4.73242 8.76794L5.79303 7.70733L10.5001 12.4144Z" fill="#525866" />
                </g>
              </svg>
            </div>
          </div>
        </div>
      </button>
      {isOpen && (
        <div id={`section-${id}`} className="px-1 pt-1.5 pb-1.5 space-y-2.5">
          {children}
        </div>
      )}
    </div>
  );
};


// Custom Radio Component
const CustomRadio: React.FC<{
  checked: boolean;
  onChange: () => void;
  label: string;
}> = ({ checked, onChange, label }) => {
  const colors = useBrandingColors();

  return (
    <div className="content-stretch flex gap-[8px] items-center relative shrink-0">
      <div className="overflow-clip relative shrink-0 size-[20px]">
        <button
          onClick={onChange}
          className="relative size-full"
        >
          <div className="absolute bg-[#e2e4e9] inset-[10%] rounded-[96px]" />
          <div className="absolute bg-white inset-[17.5%] rounded-[96px] shadow-[0px_2px_2px_0px_rgba(27,28,29,0.12)]" />
          {checked && (
            <div
              className="absolute inset-[25%] rounded-[96px]"
              style={{ backgroundColor: colors.primary }}
            />
          )}
        </button>
      </div>
      <button
        onClick={onChange}
        className="font-['SF_Pro:Regular',_sans-serif] font-normal leading-[0] relative shrink-0 text-[12px] text-black text-center text-nowrap cursor-pointer hover:text-gray-600"
      >
        <p className="leading-[16px] whitespace-pre">{label}</p>
      </button>
    </div>
  );
};

// Hoisted CustomDropdown to preserve component identity across renders
const CustomDropdown: React.FC<{
  id: string;
  value: string | number | undefined;
  onChange: (value: any) => void;
  children: React.ReactNode;
  placeholder?: string;
  className?: string;
  openUpward?: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}> = ({ id, value, onChange, children, placeholder = 'Выберите...', className = '', openUpward = false, isOpen, onToggle, onClose }) => {
  const selectedOption = React.Children.toArray(children).find(
    (child: any) => (child as any)?.props?.value === value
  ) as React.ReactElement | undefined;

  const displayValue = selectedOption ? (selectedOption as any).props.children : placeholder;

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={onToggle}
        className="bg-[#ffffff] box-border content-stretch flex flex-row gap-1.5 items-center justify-start px-3 py-[7px] relative rounded-[24px] shrink-0 border border-[#cdd0d5] hover:bg-[#f6f8fa] transition-colors cursor-pointer w-full"
      >
        <div className="box-border content-stretch flex flex-row gap-1 items-center justify-start p-0 relative shrink-0 flex-1">
          <div className="css-5e50oj font-['Inter:Medium',_sans-serif] font-medium not-italic relative shrink-0 text-[#525866] text-[14px] text-left">
            <p className="block leading-[20px] text-left">
              {displayValue}
            </p>
          </div>
        </div>
        <div className="bg-[#ffffff] box-border content-stretch flex flex-row gap-2 items-start justify-start overflow-clip p-0 relative rounded-[24px] shrink-0">
          <ArrowDownSLineIconV7 size={28} className={`w-7 h-7 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={onClose} />
          <div className={`absolute left-0 right-0 bg-[#ffffff] rounded-[24px] border border-[#e2e4e9] shadow-[0px_4px_12px_0px_rgba(0,0,0,0.08)] z-[9999] p-2 max-h-48 overflow-y-auto text-[14px] ${openUpward ? 'bottom-full mb-2' : 'top-full mt-2'}`}>
            {React.Children.toArray(children)
              .filter((child: any) => React.isValidElement(child))
              .map((child: any) => (
                <button
                  key={(child as any).props.value}
                  onClick={() => {
                    onChange((child as any).props.value);
                    onClose();
                  }}
                  className="w-full text-left px-2.5 py-1.5 rounded-[6px] text-[13px] transition-colors hover:bg-[#f6f8fa] text-[#525866]"
                >
                  {(child as any).props.children}
                </button>
              ))}
          </div>
        </>
      )}
    </div>
  );
};

// Map experience values across sources to closest equivalents
function mapExperienceAcrossSources(value: string, type: 'total' | 'position' | 'period', targetSource: string): string {
  if (!value) return '';
  // E-Staff -> HH
  if (targetSource === 'hh') {
    if (type === 'total' || type === 'position') {
      const map: Record<string, string> = {
        'more_1_year': 'between1And3',
        'more_3_years': 'between3And6',
        'more_5_years': 'moreThan6',
        'more_10_years': 'moreThan6',
      };
      return map[value] || '';
    }
    if (type === 'period') {
      const map: Record<string, string> = {
        'all_time': 'all_time',
        'last_year': 'last_year',
        'last_3_years': 'last_three_years',
        'last_5_years': 'last_six_years',
        'current': 'all_time',
      };
      return map[value] || '';
    }
  }

  // HH -> E-Staff
  if (targetSource !== 'hh') {
    if (type === 'total' || type === 'position') {
      const map: Record<string, string> = {
        'noExperience': '',
        'between1And3': 'more_1_year',
        'between3And6': 'more_3_years',
        'moreThan6': 'more_5_years',
      };
      return map[value] || '';
    }
    if (type === 'period') {
      const map: Record<string, string> = {
        'all_time': 'all_time',
        'last_year': 'last_year',
        'last_three_years': 'last_3_years',
        'last_six_years': 'last_5_years',
      };
      return map[value] || '';
    }
  }
  return '';
}

const cities = [
  'Москва','Санкт-Петербург','Новосибирск','Екатеринбург','Нижний Новгород','Казань','Челябинск','Омск','Самара','Ростов-на-Дону','Уфа','Красноярск','Воронеж','Пермь'
];

// Функция для получения человекочитаемых названий опций опыта
const getExperienceLabel = (value: string, type: 'total' | 'position' | 'period'): string => {
  const options = {
    total: [
      { value: '', label: 'Любой' },
      { value: 'noExperience', label: 'Без опыта' },
      { value: 'between1And3', label: '1-3 года' },
      { value: 'between3And6', label: '3-6 лет' },
      { value: 'moreThan6', label: 'Более 6 лет' }
    ],
    position: [
      { value: '', label: 'Любой' },
      { value: 'noExperience', label: 'Без опыта' },
      { value: 'between1And3', label: '1-3 года' },
      { value: 'between3And6', label: '3-6 лет' },
      { value: 'moreThan6', label: 'Более 6 лет' }
    ],
    period: [
      { value: '', label: 'Любой' },
      { value: 'all_time', label: 'За все время' },
      { value: 'last_year', label: 'За последний год' },
      { value: 'last_three_years', label: 'За последние 3 года' },
      { value: 'last_six_years', label: 'За последние 6 лет' }
    ]
  };

  const option = options[type].find(opt => opt.value === value);
  return option ? option.label : value;
};

// Данные отраслей для отображения названий
const INDUSTRIES_DATA = [
  {
    id: 'automotive',
    name: 'Автомобильный бизнес',
    children: [
      { id: 'auto_parts_retail', name: 'Автозапчасти, шины (розничная торговля)' },
      { id: 'auto_components_production', name: 'Автокомпоненты, запчасти (производство)' },
      { id: 'auto_components_wholesale', name: 'Автокомпоненты, запчасти, шины (продвижение, оптовая торговля)' },
      { id: 'vehicles_wholesale', name: 'Легковые, грузовые автомобили, мототехника, автобусы, троллейбусы (продвижение, оптовая торговля)' },
      { id: 'vehicles_production', name: 'Легковые, грузовые автомобили, мототехника, автобусы, троллейбусы (производство)' }
    ]
  },
  {
    id: 'hospitality',
    name: 'Гостиницы, рестораны, общепит, кейтеринг'
  },
  {
    id: 'government',
    name: 'Государственные организации'
  },
  {
    id: 'mining',
    name: 'Добывающая отрасль'
  },
  {
    id: 'housing',
    name: 'ЖКХ'
  },
  {
    id: 'it',
    name: 'Информационные технологии, системная интеграция, интернет'
  },
  {
    id: 'culture',
    name: 'Искусство, культура'
  },
  {
    id: 'forestry',
    name: 'Лесная промышленность, деревообработка'
  }
];

// Функция для получения названия отрасли по ID
const getIndustryName = (industryId: string): string => {
  // Маппинг ID отраслей на русские названия
  const industryMap: Record<string, string> = {
    'automotive': 'Автомобильный бизнес',
    'auto-parts-retail': 'Автозапчасти, шины (розничная торговля)',
    'auto-parts-production': 'Автокомпоненты, запчасти (производство)',
    'auto-parts-wholesale': 'Автокомпоненты, запчасти, шины (продвижение, оптовая торговля)',
    'vehicles-wholesale': 'Легковые, грузовые автомобили, мототехника, автобусы, троллейбусы (продвижение, оптовая торговля)',
    'vehicles-production': 'Легковые, грузовые автомобили, мототехника, автобусы, троллейбусы (производство)',
    'hotels-restaurants': 'Гостиницы, рестораны',
    'banking': 'Банки, инвестиции, лизинг',
    'it': 'IT, интернет, телеком',
    'construction': 'Строительство, недвижимость',
    'education': 'Образование, наука',
    'healthcare': 'Здравоохранение, медицина',
    'retail': 'Розничная торговля',
    'manufacturing': 'Производство',
    'transport': 'Транспорт, логистика',
    'energy': 'Энергетика',
    'agriculture': 'Сельское хозяйство',
    'media': 'СМИ, маркетинг, реклама, BTL',
    'consulting': 'Консалтинг, аудит',
    'government': 'Государственные организации',
    'non-profit': 'Некоммерческие организации',
    'other': 'Другое'
  };

  return industryMap[industryId] || industryId;
};

const getSpecializationName = (specializationId: string): string => {
  // Маппинг ID специализаций на русские названия
  const specializationMap: Record<string, string> = {
    // Автомобильный бизнес
    'automotive_spec': 'Автомобильный бизнес',
    'car_washer': 'Автомойщик',
    'auto_mechanic': 'Автослесарь, автомеханик',
    'service_advisor': 'Мастер-приемщик',
    'sales_manager': 'Менеджер по продажам, менеджер по работе с клиентами',

    // Административный персонал
    'admin': 'Административный персонал',
    'secretary': 'Секретарь',
    'assistant': 'Помощник руководителя',
    'receptionist': 'Ресепшионист',

    // Безопасность
    'security': 'Безопасность',
    'guard': 'Охранник',
    'security_manager': 'Менеджер по безопасности',
    'bodyguard': 'Телохранитель',

    // Высший и средний менеджмент
    'management': 'Высший и средний менеджмент',
    'ceo': 'Генеральный директор',
    'director': 'Директор',
    'manager': 'Менеджер',

    // Добыча сырья
    'mining_spec': 'Добыча сырья',
    'miner': 'Шахтер',
    'geologist': 'Геолог',
    'engineer': 'Горный инженер',

    // Домашний персонал
    'domestic': 'Домашний, обслуживающий персонал',
    'cleaner': 'Уборщица',
    'cook': 'Повар',
    'driver': 'Водитель',

    // Закупки
    'procurement': 'Закупки',
    'buyer': 'Покупатель',
    'procurement_manager': 'Менеджер по закупкам',
    'supply_manager': 'Менеджер по снабжению',

    // IT
    'it_spec': 'Информационные технологии',
    'developer': 'Разработчик',
    'analyst': 'Системный аналитик',
    'sysadmin': 'Системный администратор',

    // Дополнительные ID
    'automotive': 'Автомобильный бизнес',
    'hospitality': 'Гостиницы, рестораны, общепит, кейтеринг',
    'government': 'Государственные организации',
    'mining': 'Добывающая отрасль',
    'housing': 'ЖКХ',
    'it': 'Информационные технологии, системная интеграция, интернет',
    'culture': 'Искусство, культура',
    'forestry': 'Лесная промышленность, деревообработка',

    // Обработка возможных вариантов
    'administrative': 'Административный персонал', // на случай если где-то используется этот ID
  };

  return specializationMap[specializationId] || specializationId;
};

interface FiltersProps {
  value?: FiltersState;
  onChange?: (next: Partial<FiltersState>) => void;
  onSearch?: () => void;
  onClose?: () => void;
  // Пропсы для управления модальным окном специализации
  isSpecializationModalOpen?: boolean;
  onSpecializationModalOpen?: () => void;
  onSpecializationModalClose?: () => void;
  selectedSpecializations?: string[];
  onSpecializationsChange?: (specializations: string[]) => void;
  // Пропсы для управления модальным окном отрасли
  isIndustryModalOpen?: boolean;
  onIndustryModalOpen?: () => void;
  onIndustryModalClose?: () => void;
  selectedIndustries?: string[];
  onIndustriesChange?: (industries: string[]) => void;
  // География открывается через общую модалку AreaModal на уровне страницы
  onAreaModalOpen?: () => void;
  // Гражданство — модалка множественного выбора
  onCitizenshipModalOpen?: () => void;
  scrollToSection?: string;
}

const defaultState: FiltersState = defaultFiltersState;

const Filters: React.FC<FiltersProps> = ({
  value,
  onChange,
  onSearch,
  onClose,
  isSpecializationModalOpen,
  onSpecializationModalOpen,
  onSpecializationModalClose,
  selectedSpecializations = [],
  onSpecializationsChange,
  isIndustryModalOpen,
  onIndustryModalOpen,
  onIndustryModalClose,
  selectedIndustries = [],
  onIndustriesChange,
  onAreaModalOpen,
  onCitizenshipModalOpen,
  scrollToSection
}) => {
  const colors = useBrandingColors();
  const currencies = useCurrencies();
  const filters = value ?? defaultState;
  const sourceConfig = getSourceConfig(filters.source);
  const hasSection = (section: string) => sourceConfig.filterSections.includes(section as any);
  const setField = (field: keyof FiltersState, val: any) => onChange?.({ [field]: val } as Partial<FiltersState>);

  // TASKNEIROKLYUCH-812: надёжная проверка непустого значения для multi-полей
  // (totalExperience/positionExperience хранятся как comma-separated string,
  // но в legacy-путях могут приходить массивом — поэтому покрываем оба случая).
  const isMultiValueFilled = (v: unknown): boolean => {
    if (Array.isArray(v)) return v.filter(Boolean).length > 0;
    if (typeof v === 'string') return v.split(',').map(s => s.trim()).filter(Boolean).length > 0;
    return false;
  };
  const jumpersAllowed =
    filters.source === 'hh' &&
    (isMultiValueFilled(filters.totalExperience) || isMultiValueFilled(filters.positionExperience));
  const shouldShowExperienceSection =
    hasSection('positionExperience') || hasSection('avitoExperienceRange');

  // Если фильтр джамперов стал недоступен (сменили source с hh или сняли весь
  // опыт), сбрасываем флаг в false — иначе устаревший excludeJumpers=true
  // продолжит уходить в API через createApiQuery и HH-роут невидимо применит
  // isJumper.
  useEffect(() => {
    if (!jumpersAllowed && filters.excludeJumpers) {
      setField('excludeJumpers', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpersAllowed, filters.excludeJumpers]);
  
  interface SkillsInputField {
    id: string;
    type: 'any' | 'all';
    skills: string[];
    inputValue: string;
  }

  const getInitialSkillsInputs = (): SkillsInputField[] => {
    const normalizeGroupType = (t: any): 'any' | 'all' => {
      if (Array.isArray(t)) {
        if (t.includes('all') || t.includes('AND')) return 'all';
        if (t.includes('any') || t.includes('OR')) return 'any';
        return 'any';
      }
      if (t === 'all' || t === 'AND') return 'all';
      if (t === 'any' || t === 'OR') return 'any';
      return 'any';
    };

    const groupsFromValue = parseSkillGroupsRaw(value?.skillGroups);
    if (groupsFromValue && groupsFromValue.length > 0) {
      return groupsFromValue.map((group, index) => {
        const groupType = normalizeGroupType((group as any).type);
        return {
          id: String(index + 1),
          type: groupType,
          skills: Array.isArray((group as any).skills)
            ? (group as any).skills.map((s: any) => String(s).trim()).filter((s: string) => s.length > 0)
            : [],
          inputValue: ''
        };
      });
    }
    const skillsAny = value?.skillsAny || [];
    const skillsAll = value?.skillsAll || [];
    const inputs: SkillsInputField[] = [];
    if (skillsAny.length > 0) {
      inputs.push({ id: '1', type: 'any' as const, skills: skillsAny, inputValue: '' });
    }
    if (skillsAll.length > 0) {
      inputs.push({ id: '2', type: 'all' as const, skills: skillsAll, inputValue: '' });
    }
    if (inputs.length === 0) {
      inputs.push({ id: '1', type: 'any' as const, skills: [], inputValue: '' });
    }
    return inputs;
  };

  const [skillsInputs, setSkillsInputs] = useState<SkillsInputField[]>(getInitialSkillsInputs);

  const valueSkillsKey = useMemo(() => {
    const normalizeGroupType = (t: any): 'any' | 'all' => {
      if (Array.isArray(t)) {
        if (t.includes('all') || t.includes('AND')) return 'all';
        if (t.includes('any') || t.includes('OR')) return 'any';
        return 'any';
      }
      if (t === 'all' || t === 'AND') return 'all';
      if (t === 'any' || t === 'OR') return 'any';
      return 'any';
    };

    const groupsForKey = parseSkillGroupsRaw(value?.skillGroups);
    if (groupsForKey && groupsForKey.length > 0) {
      return JSON.stringify(
        groupsForKey.map((g: any) => ({
          type: normalizeGroupType(g?.type),
          skills: Array.isArray(g?.skills)
            ? g.skills.map((s: any) => String(s).trim()).filter((s: string) => s.length > 0).sort()
            : []
        }))
      );
    }

    const skillsAny = value?.skillsAny ?? [];
    const skillsAll = value?.skillsAll ?? [];
    return JSON.stringify({
      skillsAny: skillsAny.map((s: any) => String(s).trim()).filter((s: string) => s.length > 0).sort(),
      skillsAll: skillsAll.map((s: any) => String(s).trim()).filter((s: string) => s.length > 0).sort(),
    });
  }, [value?.skillGroups, value?.skillsAny, value?.skillsAll]);

  const lastAppliedValueSkillsKeyRef = useRef<string>(valueSkillsKey);

  useEffect(() => {
    if (lastAppliedValueSkillsKeyRef.current === valueSkillsKey) return;
    setSkillsInputs(getInitialSkillsInputs());
    lastAppliedValueSkillsKeyRef.current = valueSkillsKey;
  }, [valueSkillsKey]);

  const lastEmittedSkillsKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!onChange) return;

    const skillGroups = skillsInputs
      .filter(input => input.skills.length > 0)
      .map(input => ({
        type: input.type,
        skills: input.skills
      }));

    const emittedKey = JSON.stringify(
      skillGroups.map(g => ({
        type: g.type,
        skills: [...g.skills].map(s => String(s).trim()).filter(s => s.length > 0).sort()
      }))
    );

    if (lastEmittedSkillsKeyRef.current === emittedKey) return;
    lastEmittedSkillsKeyRef.current = emittedKey;
    lastAppliedValueSkillsKeyRef.current = valueSkillsKey;

    const base = (value ?? ({} as FiltersState));
    if (skillGroups.length > 0) {
      onChange({ ...base, skillGroups, skillsAny: [], skillsAll: [] });
    } else if (skillsInputs.every(input => input.skills.length === 0)) {
      onChange({ ...base, skillGroups: undefined, skillsAny: [], skillsAll: [] });
    }
  }, [skillsInputs, onChange, value, valueSkillsKey]);

  const addSkillToInput = (inputId: string) => {
    setSkillsInputs(prev => prev.map(input => {
      if (input.id === inputId) {
        const parts = input.inputValue
          .split(',')
          .map((part) => part.trim())
          .filter((part) => part.length > 0);

        if (parts.length === 0) {
          return input;
        }

        const uniqueToAdd = parts.filter((topic) => !input.skills.includes(topic));
        if (uniqueToAdd.length === 0) {
          return { ...input, inputValue: '' };
        }

        return { ...input, skills: [...input.skills, ...uniqueToAdd], inputValue: '' };
      }
      return input;
    }));
  };

  const removeSkillFromInput = (inputId: string, skill: string) => {
    setSkillsInputs(prev => prev.map(input => {
      if (input.id === inputId) {
        return { ...input, skills: input.skills.filter(s => s !== skill) };
      }
      return input;
    }));
  };

  const updateInputValue = (inputId: string, value: string) => {
    setSkillsInputs(prev => prev.map(input => {
      if (input.id === inputId) {
        return { ...input, inputValue: value };
      }
      return input;
    }));
  };

  const toggleInputType = (inputId: string) => {
    setSkillsInputs(prev => prev.map(input => {
      if (input.id === inputId) {
        return { ...input, type: input.type === 'any' ? 'all' : 'any' };
      }
      return input;
    }));
  };

  const addNewSkillsInput = () => {
    const newId = String(Math.max(...skillsInputs.map(i => parseInt(i.id)), 0) + 1);
    setSkillsInputs(prev => [...prev, { id: newId, type: 'any', skills: [], inputValue: '' }]);
  };

  const removeSkillsInput = (inputId: string) => {
    if (skillsInputs.length > 1) {
      setSkillsInputs(prev => prev.filter(input => input.id !== inputId));
    }
  };
  // Инициализируем strictSearch на основе текущего состояния навыков
  const [strictSearch, setStrictSearch] = useState(() => {
    const skillsAll = value?.skillsAll || [];
    const skillsAny = value?.skillsAny || [];
    // Если есть навыки в skillsAll и нет в skillsAny - значит строгий поиск
    return skillsAll.length > 0 && skillsAny.length === 0;
  });

  const handleStrictSearchToggle = useCallback((checked: boolean) => {
    setStrictSearch(checked);

    if (!onChange || !value) return;

    const currentSkillsAny = value.skillsAny || [];
    const currentSkillsAll = value.skillsAll || [];

    // Only move skills if there are any
    if (currentSkillsAny.length === 0 && currentSkillsAll.length === 0) return;

    const allSkills = Array.from(new Set([...currentSkillsAny, ...currentSkillsAll]));

    if (checked) {
      // Move all to strict (skillsAll)
      onChange({
        ...value,
        skillsAny: [],
        skillsAll: allSkills
      });
    } else {
      // Move all to non-strict (skillsAny)
      onChange({
        ...value,
        skillsAny: allSkills,
        skillsAll: []
      });
    }
  }, [onChange, value]);

  const [industryNames, setIndustryNames] = useState<Record<string, string>>({});
  const [specializationNames, setSpecializationNames] = useState<Record<string, string>>({});
  const [geoDisplayCount, setGeoDisplayCount] = useState<number>(0);
  const [metroCities, setMetroCities] = useState<MetroCityOption[]>([]);
  const [countries, setCountries] = useState<CountryOption[]>([]);

  useEffect(() => {
    const workFormat = (value as any)?.workFormat;
    const needsMetro = workFormat === 'Офис' || workFormat === 'Гибрид';
    if (!needsMetro) return;
    if (metroCities.length > 0) return;
    fetchHHMetroCities()
      .then(setMetroCities)
      .catch((e) => {
        console.error('Ошибка загрузки справочника метро:', e);
      });
  }, [(value as any)?.workFormat, metroCities.length]);

  useEffect(() => {
    fetchHHCountries()
      .then(setCountries)
      .catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    const ids = (value as any)?.cityIds || [];
    if (Array.isArray(ids) && ids.length > 0) {
      (async () => {
        try {
          const compressor = sourceConfig.postProcessors?.compressCityIds
            ?? (async (values: string[]) => values);
          const compressed = await compressor(ids as string[]);
          setGeoDisplayCount(Array.isArray(compressed) ? compressed.length : ids.length);
        } catch {
          setGeoDisplayCount(ids.length);
        }
      })();
    } else {
      setGeoDisplayCount(0);
    }
  }, [(value as any)?.cityIds, sourceConfig.postProcessors]);

  // Автоматическая прокрутка к нужной секции
  useEffect(() => {
    if (scrollToSection) {
      const timer = setTimeout(() => {
        const element = document.querySelector(`[data-section="${scrollToSection}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [scrollToSection]);

  useEffect(() => {
    const load = async () => {
      const ids: string[] = [];
      if (selectedIndustries && selectedIndustries.length > 0) ids.push(...selectedIndustries);
      if ((filters as any).industry) ids.push((filters as any).industry as string);
      const newNames: Record<string, string> = {};
      for (const id of ids) {
        if (id && !industryNames[id]) {
          try {
            const name = await getIndustryNameById(id);
            newNames[id] = name;
          } catch {
            newNames[id] = id;
          }
        }
      }
      if (Object.keys(newNames).length > 0) setIndustryNames(prev => ({ ...prev, ...newNames }));
    };
    void load();
  }, [selectedIndustries, (filters as any).industry, industryNames]);

  useEffect(() => {
    const load = async () => {
      const sourceId = filters.source || 'hh';
      const ids: string[] = [];
      if (selectedSpecializations && selectedSpecializations.length > 0) ids.push(...selectedSpecializations);
      const newNames: Record<string, string> = {};
      for (const id of ids) {
        const cacheKey = getSpecializationDisplayCacheKey(sourceId, id);
        if (id && !specializationNames[cacheKey]) {
          try {
            const name = await getSpecializationDisplayName(sourceId, id);
            newNames[cacheKey] = name || id;
          } catch {
            newNames[cacheKey] = id;
          }
        }
      }
      if (Object.keys(newNames).length > 0) setSpecializationNames(prev => ({ ...prev, ...newNames }));
    };
    void load();
  }, [selectedSpecializations, specializationNames, filters.source]);

  // Состояние для открытых дропдаунов
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});

  // Состояние для управления аккордеоном (независимые секции)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    'search-params': true,
    'experience': false,
    'conditions': false,
    'demography': false,
    'companies': false,
    'hh-params': false
  });

  // Состояние для модальных окон (только для FilterModal)
  // Модальные окна отрасли и специализации теперь управляются из FiltersModal

  // Функция для переключения секций аккордеона
  const toggleAccordionSection = (sectionId: string) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const renderAvitoCheckboxGroup = (
    section: string,
    field: keyof FiltersState,
    title: string,
    options: Array<{ value: string; label: string }>
  ) => {
    if (!hasSection(section)) return null;

    const list = Array.isArray(filters[field]) ? (filters[field] as string[]) : [];

    return (
      <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full">
        <div className="flex flex-col font-['Inter:Medium',_sans-serif] font-medium justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black tracking-[-0.084px] w-full">
          <p className="leading-[20px]">{title}</p>
        </div>
        <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full">
          {options.map((opt) => (
            <CustomCheckbox
              key={opt.value}
              checked={list.includes(opt.value)}
              onChange={(checked) => {
                const next = checked
                  ? [...list, opt.value]
                  : list.filter((v) => v !== opt.value);
                setField(field, next);
              }}
              label={opt.label}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderAvitoDriverCategories = () => {
    if (!hasSection('avitoDriverLicenceCategory')) return null;

    const list = filters.avitoDriverLicenceCategory || [];

    return (
      <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full">
        <div className="flex flex-col font-['Inter:Medium',_sans-serif] font-medium justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black tracking-[-0.084px] w-full">
          <p className="leading-[20px]">Категории прав</p>
        </div>
        <div className="flex flex-wrap gap-[8px] relative shrink-0 w-full">
          {AVITO_DRIVER_CATEGORIES.map((cat) => {
            const checked = list.includes(cat);
            return (
              <button
                key={cat}
                type="button"
                onClick={() => {
                  const next = checked
                    ? list.filter((v) => v !== cat)
                    : [...list, cat];
                  setField('avitoDriverLicenceCategory', next);
                }}
                className={`px-[12px] py-[6px] rounded-[10px] border text-[14px] transition-colors ${
                  checked
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-[#525866] border-[#e2e4e9] hover:bg-[#f6f8fa]'
                }`}
              >
                {cat.toUpperCase()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="bg-[#f5f6f1] box-border flex flex-col items-start relative size-full h-full">
      {/* Header with close button */}
      {onClose && (
        <div className="flex items-center justify-between p-4 w-full">
          <h2 className="text-2xl font-semibold text-gray-900">Фильтры</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
      )}

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto w-full px-[16px] pt-[8px] pb-[16px]">
        <div className="content-stretch flex flex-col gap-[8px] items-start relative shrink-0 w-full">
          <div className="content-stretch flex flex-col gap-[10px] items-start relative shrink-0 w-full">
            <div className="content-stretch flex flex-col gap-[6px] items-start relative shrink-0 w-full">
              {/* Reset Filters Button */}
              <button
                onClick={() => onChange?.(getDefaultFiltersForSource(filters.source || 'hh'))}
                className="bg-[#e2e4e9] box-border content-stretch flex gap-[4px] items-center justify-center overflow-clip px-[12px] py-[8px] relative rounded-[24px] shrink-0 w-full hover:bg-[#d1d5db] transition-colors"
              >
                <div className="overflow-clip relative shrink-0 size-[28px]">
                  <div className="absolute inset-[20%]">
                    <svg className="block max-w-none size-full" fill="none" viewBox="0 0 20 20">
                      <path d="M15 5L5 15M5 5l10 10" stroke="#525866" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
                <div className="box-border content-stretch flex gap-[8px] items-center justify-center px-[4px] py-0 relative shrink-0">
                  <div className="css-2x1u1o font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#525866] text-[14px] text-center text-nowrap tracking-[-0.084px]">
                    <p className="leading-[20px] whitespace-pre">Сбросить фильтры</p>
                  </div>
                </div>
              </button>

              {/* Custom search mode - existing filters content here */}
            </div>
          </div>

        <div className="content-stretch flex flex-col gap-[24px] items-start relative shrink-0 w-full">
          {/* Секция "Параметры поиска" */}
          <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full">
            <div className="font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#868c98] text-[16px] tracking-[-0.176px] w-full">
              <p className="leading-[24px]">Параметры поиска</p>
            </div>

            <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full" data-section="skills">
              {/* Поле "Должность" */}
              <div className="content-stretch flex flex-col gap-[4px] items-center relative shrink-0 w-full">
                <div className="content-stretch flex gap-px items-center relative shrink-0 w-full">
                  <div className="flex flex-col font-['Inter:Regular',_sans-serif] font-normal justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black text-nowrap tracking-[-0.084px]">
                    <p className="leading-[20px] whitespace-pre">Должность</p>
                  </div>
                </div>
                <div className="bg-white relative rounded-[10px] shrink-0 w-full">
                  <div className="box-border content-stretch flex gap-[8px] items-center overflow-clip pl-[12px] pr-[10px] py-[10px] relative w-full">
                    <input
                      type="text"
                      placeholder="Введите должность"
                      value={value?.positionName || ''}
                      onChange={(e) => onChange?.({ ...value, positionName: e.target.value })}
                      className="basis-0 font-['Inter:Regular',_sans-serif] font-normal grow leading-[0] min-h-px min-w-px not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-[#525866] text-[14px] text-left tracking-[-0.084px] outline-none bg-transparent placeholder:text-[#868c98]"
                    />
                  </div>
                  <div aria-hidden="true" className="absolute border border-[#e2e4e9] border-solid inset-0 pointer-events-none rounded-[10px] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)]" />
                </div>
              </div>

              {/* TASKNEIROKLYUCH-790: «Предполагаемая должность» для E-Staff —
                  отдельный фильтр по `desired_position_name` (что кандидат
                  ищет), независимый от «Должность» (фактическая работа).
                  HH-эндпоинт этого поля не имеет, поэтому видим только для
                  source='estaff'. State чистится при выходе из E-Staff в
                  `SearchInterface.tsx` (handler смены source). */}
              {value?.source === 'estaff' && (
                <div className="content-stretch flex flex-col gap-[4px] items-center relative shrink-0 w-full">
                  <div className="content-stretch flex gap-px items-center relative shrink-0 w-full">
                    <div className="flex flex-col font-['Inter:Regular',_sans-serif] font-normal justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black text-nowrap tracking-[-0.084px]">
                      <p className="leading-[20px] whitespace-pre">Предполагаемая должность</p>
                    </div>
                  </div>
                  <div className="bg-white relative rounded-[10px] shrink-0 w-full">
                    <div className="box-border content-stretch flex gap-[8px] items-center overflow-clip pl-[12px] pr-[10px] py-[10px] relative w-full">
                      <input
                        type="text"
                        placeholder="Желаемая должность кандидата (из CV)"
                        value={value?.expectedPositionName || ''}
                        onChange={(e) => onChange?.({ ...value, expectedPositionName: e.target.value })}
                        className="basis-0 font-['Inter:Regular',_sans-serif] font-normal grow leading-[0] min-h-px min-w-px not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-[#525866] text-[14px] text-left tracking-[-0.084px] outline-none bg-transparent placeholder:text-[#868c98]"
                      />
                    </div>
                    <div aria-hidden="true" className="absolute border border-[#e2e4e9] border-solid inset-0 pointer-events-none rounded-[10px] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)]" />
                  </div>
                </div>
              )}

              {hasSection('avitoKeywords') && (
                <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full">
                  <div className="flex flex-col font-['Inter:Regular',_sans-serif] font-normal justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black text-nowrap tracking-[-0.084px]">
                    <p className="leading-[20px] whitespace-pre">Ключевые слова</p>
                  </div>
                  <TagsInput
                    value={filters.avitoKeywords || []}
                    onChange={(tags) => setField('avitoKeywords', tags)}
                    placeholder="Введите слово"
                    showHint={false}
                    variant="skill"
                  />
                </div>
              )}

              {hasSection('avitoExcludedWords') && (
                <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full">
                  <div className="flex flex-col font-['Inter:Regular',_sans-serif] font-normal justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black text-nowrap tracking-[-0.084px]">
                    <p className="leading-[20px] whitespace-pre">Скрыть объявления со словами</p>
                  </div>
                  <TagsInput
                    value={filters.avitoExcludedWords || []}
                    onChange={(tags) => setField('avitoExcludedWords', tags)}
                    placeholder="Введите слово"
                    showHint={false}
                    variant="skill"
                  />
                </div>
              )}

              {/* TASKNEIROKLYUCH-670: чекбокс на уровне секции, а не внутри блока с input'ом
                  (там items-center → не выровнялось бы влево). */}
              {hasSection('additional') && (
                <CustomCheckbox
                  checked={value?.currentPositionOnly || false}
                  onChange={(checked) => onChange?.({ ...value, currentPositionOnly: checked })}
                  label="Только текущая должность"
                />
              )}

              {/* Группы навыков */}
              {hasSection('skills') && (
              <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full">
                <div className="flex flex-col font-['Inter:Regular',_sans-serif] font-normal justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black text-nowrap tracking-[-0.084px]">
                  <p className="leading-[20px] whitespace-pre">Ключевые навыки</p>
                </div>
                <div className="space-y-4 w-full">
                  {skillsInputs.map((input, index) => (
                    <div key={input.id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => toggleInputType(input.id)}
                          className="flex items-center gap-1 text-sm font-medium transition-colors"
                          style={{ color: colors.primary }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.8'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                        >
                          <span className="text-[14px]">{input.type === 'any' ? 'Поиск по любому из навыков' : 'Поиск по всем навыкам'}</span>
                          <ArrowDownSLineIconV7 className="h-4 w-4" />
                        </button>
                        {skillsInputs.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeSkillsInput(input.id)}
                            className="text-[#6b7280] hover:text-[#ef4444] transition-colors text-sm"
                          >
                            Удалить группу
                          </button>
                        )}
                        </div>

                      <div className="bg-white relative rounded-[10px] shrink-0 w-full">
                        <div className="box-border content-stretch flex gap-[8px] items-center overflow-clip pl-[12px] pr-[10px] py-[10px] relative w-full">
                          <div className="basis-0 font-['Inter:Regular',_sans-serif] font-normal grow leading-[0] min-h-px min-w-px not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-[#525866] text-[14px] text-left tracking-[-0.084px]">
                            <div className="flex flex-wrap gap-2 items-center min-h-[20px]">
                              {input.skills.map((skill, skillIndex) => (
                                <React.Fragment key={skill}>
                                  <span className="inline-flex items-center rounded-full text-[12px] px-2 py-1 whitespace-nowrap" style={{ backgroundColor: colors.primary + '20', color: colors.primary }}>
                                    {skill}
                                    <button
                                      type="button"
                                      onClick={() => removeSkillFromInput(input.id, skill)}
                                      className="ml-1 rounded-full p-0.5"
                                      style={{ color: colors.primary }}
                                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.primary + '40'; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                    >
                                      <XMarkIconSolid className="h-3 w-3" />
                                    </button>
                                  </span>
                                  {skillIndex < input.skills.length - 1 && (
                                    <span className="text-sm text-[#525866]">
                                      {input.type === 'any' ? 'или' : 'и'}
                                    </span>
                                  )}
                                </React.Fragment>
                              ))}
                              <input
                                type="text"
                                value={input.inputValue}
                                onChange={(e) => updateInputValue(input.id, e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addSkillToInput(input.id);
                                  }
                                }}
                                placeholder={input.skills.length === 0 ? (input.type === 'any' ? "любой из навыка (ИЛИ)" : "Все навыки обязательны (И)") : ""}
                                className="flex-1 min-w-0 outline-none bg-transparent text-[14px] leading-[20px] text-[#525866] placeholder:text-[#868c98]"
                              />
                            </div>
                          </div>
                        </div>
                        <div aria-hidden="true" className="absolute border border-[#e2e4e9] border-solid inset-0 pointer-events-none rounded-[10px] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)]" />
                      </div>
                      <div className="box-border content-stretch flex gap-[4px] items-start px-0 py-[2px] relative shrink-0 w-full">
                        <div className="basis-0 flex flex-col font-['Inter:Regular',_sans-serif] font-normal grow justify-center leading-[0] min-h-px min-w-px not-italic relative shrink-0 text-[#525866] text-[12px]">
                          <p className="leading-[16px]">{input.type === 'any' ? 'Любой из навыков (ИЛИ)' : 'Все навыки обязательны (И)'}</p>
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addNewSkillsInput}
                    className="flex items-center gap-1 text-sm font-medium transition-colors mt-2"
                    style={{ color: colors.primary }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.8'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: colors.primary }}>
                      <path d="M8 4V12M4 8H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <span>Добавить еще группу</span>
                  </button>
                </div>
              </div>
              )}
              {/* Отрасль */}
              {hasSection('industry') && (
              <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full">
                <div className="content-stretch flex gap-px items-center relative shrink-0 w-full">
                  <div className="flex flex-col font-['Inter:Regular',_sans-serif] font-normal justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black text-nowrap tracking-[-0.084px]">
                    <p className="leading-[20px] whitespace-pre">Отрасль</p>
                  </div>
                </div>
                <button
                  onClick={() => onIndustryModalOpen?.()}
                  className="bg-white relative rounded-[10px] shrink-0 w-full hover:bg-[#f6f8fa] transition-colors cursor-pointer"
                >
                  <div className="box-border content-stretch flex gap-[8px] items-center overflow-clip pl-[12px] pr-[10px] py-[10px] relative w-full">
                    <div className="basis-0 font-['Inter:Regular',_sans-serif] font-normal grow leading-[0] min-h-px min-w-px not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-[14px] text-left tracking-[-0.084px]">
                      <p className={`[white-space-collapse:collapse] leading-[20px] overflow-ellipsis overflow-hidden text-left ${(value?.industry || selectedIndustries?.length > 0) ? 'text-[#525866]' : 'text-[#868c98]'}`}>
                        {selectedIndustries?.length > 0
                          ? (selectedIndustries.length === 1
                              ? (industryNames[selectedIndustries[0]] || selectedIndustries[0])
                              : `Выбрано: ${selectedIndustries.length}`)
                          : value?.industry
                            ? (industryNames[value.industry] || value.industry)
                            : 'Выберите отрасль...'
                        }
                      </p>
                    </div>
                    <div className="overflow-clip relative shrink-0 size-[20px]">
                      <div className="absolute inset-[10%_5%_10%_5%]">
                        <svg className="block max-w-none size-full" fill="none" viewBox="0 0 20 20">
                          <path d="M10.5001 12.4144L15.2072 7.70733L16.2678 8.76794L10.5001 14.5356L4.73242 8.76794L5.79303 7.70733L10.5001 12.4144Z" fill="#525866"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div aria-hidden="true" className="absolute border border-[#e2e4e9] border-solid inset-0 pointer-events-none rounded-[10px] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)]" />
                </button>
                {/* Период опыта в отрасли */}
                {hasSection('experiencePeriod') && (
                <div className={`w-full ${!selectedIndustries || selectedIndustries.length === 0 ? 'opacity-50 pointer-events-none' : ''}`} title={!selectedIndustries || selectedIndustries.length === 0 ? 'Сначала выберите отрасль' : ''}>
                <CustomSelect
                  label="Период опыта в отрасли"
                  value={filters.experiencePeriod || ''}
                  onChange={(value) => setField('experiencePeriod', value)}
                  options={HH_EXPERIENCE_PERIOD_OPTIONS}
                  placeholder="Выберите период опыта в отрасли..."
                />
                </div>
                )}
                <CustomCheckbox
                  checked={filters.filterByExpIndustry || false}
                  onChange={(checked) => setField('filterByExpIndustry', checked)}
                  label="Искать по последним местам работы"
                  disabled={!selectedIndustries || selectedIndustries.length === 0}
                />
              </div>
              )}

              {/* Специализация */}
              {hasSection('specialization') && (
              <div className="content-stretch flex flex-col gap-[4px] items-center relative shrink-0 w-full">
                <div className="content-stretch flex gap-px items-center relative shrink-0 w-full">
                  <div className="flex flex-col font-['Inter:Regular',_sans-serif] font-normal justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black text-nowrap tracking-[-0.084px]">
                    <p className="leading-[20px] whitespace-pre">Специализация</p>
                  </div>
                </div>
          <button
            onClick={() => onSpecializationModalOpen?.()}
            className="bg-white relative rounded-[10px] shrink-0 w-full hover:bg-[#f6f8fa] transition-colors cursor-pointer"
          >
                  <div className="box-border content-stretch flex gap-[8px] items-center overflow-clip pl-[12px] pr-[10px] py-[10px] relative w-full">
                    <div className="basis-0 font-['Inter:Regular',_sans-serif] font-normal grow leading-[0] min-h-px min-w-px not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-[14px] text-left tracking-[-0.084px]">
                      <p className={`[white-space-collapse:collapse] leading-[20px] overflow-ellipsis overflow-hidden text-left ${selectedSpecializations.length > 0 ? 'text-[#525866]' : 'text-[#868c98]'}`}>
                        {selectedSpecializations.length > 0
                          ? (selectedSpecializations.length === 1
                              ? (specializationNames[getSpecializationDisplayCacheKey(filters.source || 'hh', selectedSpecializations[0])] || selectedSpecializations[0])
                              : `Выбрано: ${selectedSpecializations.length}`)
                          : 'Выберите специализацию...'
                        }
                      </p>
                    </div>
                    <div className="overflow-clip relative shrink-0 size-[20px]">
                      <div className="absolute inset-[10%_5%_10%_5%]">
                        <svg className="block max-w-none size-full" fill="none" viewBox="0 0 20 20">
                          <path d="M10.5001 12.4144L15.2072 7.70733L16.2678 8.76794L10.5001 14.5356L4.73242 8.76794L5.79303 7.70733L10.5001 12.4144Z" fill="#525866"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div aria-hidden="true" className="absolute border border-[#e2e4e9] border-solid inset-0 pointer-events-none rounded-[10px] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)]" />
                </button>
              </div>
              )}
            </div>
          </div>

          {/* Секция "Опыт" */}
          {shouldShowExperienceSection && (
            <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full">
              <div className="font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#868c98] text-[16px] tracking-[-0.176px] w-full">
                <p className="leading-[24px]">Опыт</p>
              </div>
              {hasSection('positionExperience') && (
              <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full">
                {/* Общий опыт работы */}
                <div className="content-stretch flex flex-col gap-[8px] items-start relative shrink-0 w-full">
                  <label className="font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#868c98] text-[14px] tracking-[-0.154px]">
                    Общий опыт работы
                  </label>
                  <div className="content-stretch flex flex-col gap-[8px] items-start relative shrink-0 w-full">
                    {HH_EXPERIENCE_OPTIONS.filter(opt => opt.value !== '').map((option) => {
                      const currentValue = filters.totalExperience || '';
                      const selectedValues = currentValue ? currentValue.split(',').map((s: string) => s.trim()).filter(Boolean) : [];
                      const isChecked = selectedValues.includes(option.value);
                      return (
                        <CustomCheckbox
                          key={option.value}
                          checked={isChecked}
                          onChange={(checked) => {
                            const current = filters.totalExperience || '';
                            const currentArray = current ? current.split(',').map((s: string) => s.trim()).filter(Boolean) : [];
                            const newArray = checked
                              ? [...currentArray, option.value]
                              : currentArray.filter((v: string) => v !== option.value);
                            setField('totalExperience', newArray.join(','));
                          }}
                          label={option.label}
                        />
                      );
                    })}
                  </div>
                </div>

                {/* Опыт работы в должности */}
                {hasSection('positionExperience') && (
                <div className="content-stretch flex flex-col gap-[8px] items-start relative shrink-0 w-full">
                  <label className="font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#868c98] text-[14px] tracking-[-0.154px]">
                    Опыт работы в должности
                  </label>
                  <div className="content-stretch flex flex-col gap-[8px] items-start relative shrink-0 w-full">
                    {HH_POSITION_EXPERIENCE_OPTIONS.filter(opt => opt.value !== '').map((option) => {
                      const currentValue = filters.positionExperience || '';
                      const selectedValues = currentValue ? currentValue.split(',').map((s: string) => s.trim()).filter(Boolean) : [];
                      const isChecked = selectedValues.includes(option.value);
                      return (
                        <CustomCheckbox
                          key={option.value}
                          checked={isChecked}
                          onChange={(checked) => {
                            const current = filters.positionExperience || '';
                            const currentArray = current ? current.split(',').map((s: string) => s.trim()).filter(Boolean) : [];
                            const newArray = checked
                              ? [...currentArray, option.value]
                              : currentArray.filter((v: string) => v !== option.value);
                            setField('positionExperience', newArray.join(','));
                          }}
                          label={option.label}
                        />
                      );
                    })}
                  </div>
                </div>
                )}

                {/* TASKNEIROKLYUCH-812: «Исключить кандидатов-джамперов» — пост-фильтр HH
                    (см. routes/hh.js → isJumper, последние 3 места < 12 мес).
                    Был удалён в a9c1302 при чистке фильтров под E-Staff и заодно
                    пропал и для HH; возвращаем обратно. Доступен ТОЛЬКО для HH
                    и только когда выбран опыт (см. jumpersAllowed выше). При
                    переходе в disabled state useEffect сбрасывает поле, чтобы
                    устаревший true не утёк в createApiQuery. */}
                <CustomCheckbox
                  checked={jumpersAllowed ? (filters.excludeJumpers || false) : false}
                  onChange={(checked) => setField('excludeJumpers', checked)}
                  label="Исключить кандидатов-джамперов"
                  disabled={!jumpersAllowed}
                />

              </div>
              )}

              {hasSection('avitoExperienceRange') && (
                <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full">
                  <div className="flex flex-col font-['Inter:Medium',_sans-serif] font-medium justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black tracking-[-0.084px] w-full">
                    <p className="leading-[20px]">Стаж работы (лет)</p>
                  </div>
                  <div className="content-stretch flex gap-[8px] items-center relative shrink-0 w-full">
                    <input
                      type="number"
                      min={0}
                      max={50}
                      placeholder="от"
                      value={filters.avitoExperienceMin || ''}
                      onChange={(e) => setField('avitoExperienceMin', e.target.value)}
                      className="bg-white rounded-[10px] border border-[#e2e4e9] basis-0 grow px-[12px] py-[10px] text-[14px] text-[#525866] outline-none"
                  />
                    <input
                      type="number"
                      min={0}
                      max={50}
                      placeholder="до"
                      value={filters.avitoExperienceMax || ''}
                      onChange={(e) => setField('avitoExperienceMax', e.target.value)}
                      className="bg-white rounded-[10px] border border-[#e2e4e9] basis-0 grow px-[12px] py-[10px] text-[14px] text-[#525866] outline-none"
                  />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Секция "Условия" */}
          <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full">
            <div className="font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#868c98] text-[16px] tracking-[-0.176px] w-full">
              <p className="leading-[24px]">Условия</p>
            </div>

            <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full">
              {/* Зарплатные ожидания */}
              <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full">
                <div className="flex items-center gap-2 w-full">
                  <span className="font-['Inter:Regular',_sans-serif] font-normal text-[14px] text-black tracking-[-0.084px] leading-[20px]">Зарплатные ожидания</span>
                  {filters.source !== 'avito' && (
                    <select
                      value={value?.salaryCurrency || 'RUR'}
                      onChange={(e) => onChange?.({ ...value, salaryCurrency: e.target.value })}
                      className="px-2 py-0.5 border border-[#e2e4e9] rounded-lg text-[12px] bg-white outline-none text-[#525866] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)]"
                      title="Валюта"
                    >
                      {currencies.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="content-stretch flex gap-[8px] items-center relative shrink-0 w-full">
                  <div className="basis-0 content-stretch flex flex-col gap-[4px] grow items-center min-h-px min-w-px relative shrink-0">
                    <div className="bg-white relative rounded-[10px] shrink-0 w-full">
                      <div className="box-border content-stretch flex gap-[8px] items-center overflow-clip pl-[12px] pr-[10px] py-[10px] relative w-full">
                        <input
                          type="text"
                          placeholder="от"
                          value={formatNumberWithSpaces(value?.salaryFrom)}
                          inputMode="numeric"
                          pattern="\\d*"
                          onKeyDown={(e) => {
                            const allowedCtrl = e.ctrlKey || e.metaKey;
                            const allowedKeys = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Home','End'];
                            if (allowedCtrl) {
                              const key = e.key.toLowerCase();
                              if (key === 'a' || key === 'c' || key === 'v' || key === 'x') return;
                            }
                            if (allowedKeys.includes(e.key)) return;
                            if (!/^[0-9\s]$/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onPaste={(e) => {
                            e.preventDefault();
                            const text = (e.clipboardData || (window as any).clipboardData)?.getData('text') || '';
                            const digits = text.replace(/\D/g, '');
                            onChange?.({ ...value, salaryFrom: digits });
                          }}
                          onChange={(e) => {
                            const raw = parseFormattedNumber(e.target.value);
                            if (raw === '') { onChange?.({ ...value, salaryFrom: '' }); return; }
                            const num = parseInt(raw, 10);
                            if (Number.isNaN(num) || num < 0) { onChange?.({ ...value, salaryFrom: '' }); return; }
                            onChange?.({ ...value, salaryFrom: String(num) });
                          }}
                          className="basis-0 font-['Inter:Regular',_sans-serif] font-normal grow leading-[0] min-h-px min-w-px not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-[#525866] text-[14px] text-left tracking-[-0.084px] outline-none bg-transparent placeholder:text-[#868c98]"
                        />
                      </div>
                      <div aria-hidden="true" className="absolute border border-[#e2e4e9] border-solid inset-0 pointer-events-none rounded-[10px] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)]" />
                    </div>
                  </div>
                  <div className="basis-0 content-stretch flex flex-col gap-[4px] grow items-center min-h-px min-w-px relative shrink-0">
                    <div className="bg-white relative rounded-[10px] shrink-0 w-full">
                      <div className="box-border content-stretch flex gap-[8px] items-center overflow-clip pl-[12px] pr-[10px] py-[10px] relative w-full">
                        <input
                          type="text"
                          placeholder="до"
                          value={formatNumberWithSpaces(value?.salaryTo)}
                          inputMode="numeric"
                          pattern="\\d*"
                          onKeyDown={(e) => {
                            const allowedCtrl = e.ctrlKey || e.metaKey;
                            const allowedKeys = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Home','End'];
                            if (allowedCtrl) {
                              const key = e.key.toLowerCase();
                              if (key === 'a' || key === 'c' || key === 'v' || key === 'x') return;
                            }
                            if (allowedKeys.includes(e.key)) return;
                            if (!/^[0-9\s]$/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onPaste={(e) => {
                            e.preventDefault();
                            const text = (e.clipboardData || (window as any).clipboardData)?.getData('text') || '';
                            const digits = text.replace(/\D/g, '');
                            onChange?.({ ...value, salaryTo: digits });
                          }}
                          onChange={(e) => {
                            const raw = parseFormattedNumber(e.target.value);
                            if (raw === '') { onChange?.({ ...value, salaryTo: '' }); return; }
                            const num = parseInt(raw, 10);
                            if (Number.isNaN(num) || num < 0) { onChange?.({ ...value, salaryTo: '' }); return; }
                            onChange?.({ ...value, salaryTo: String(num) });
                          }}
                          className="basis-0 font-['Inter:Regular',_sans-serif] font-normal grow leading-[0] min-h-px min-w-px not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-[#525866] text-[14px] text-left tracking-[-0.084px] outline-none bg-transparent placeholder:text-[#868c98]"
                        />
                      </div>
                      <div aria-hidden="true" className="absolute border border-[#e2e4e9] border-solid inset-0 pointer-events-none rounded-[10px] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)]" />
                    </div>
                  </div>
                </div>
                <div className="mt-2">
                  <CustomCheckbox
                    checked={value?.incomeSpecified || false}
                    onChange={(checked) => onChange?.({ ...value, incomeSpecified: checked, hasSalary: checked })}
                    label="Указана ЗП"
                  />
                </div>
              </div>

              {hasSection('education') && (
              <div className="content-stretch flex flex-col gap-[4px] items-center relative shrink-0 w-full">
                <div className="content-stretch flex gap-px items-center relative shrink-0 w-full">
                  <div className="flex flex-col font-['Inter:Regular',_sans-serif] font-normal justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black text-nowrap tracking-[-0.084px]">
                    <p className="leading-[20px] whitespace-pre">Образование</p>
                  </div>
                </div>
                <div className="content-stretch flex flex-col gap-[8px] relative shrink-0 w-full">
                  {EDUCATION_OPTIONS.map((opt) => {
                    const currentList = Array.isArray(filters.education)
                      ? filters.education
                      : (filters.education ? [String(filters.education)] : []);
                    const checked = currentList.includes(opt.value);
                    return (
                      <CustomCheckbox
                        key={opt.value}
                        checked={checked}
                        onChange={(nextChecked) => {
                          const next = nextChecked
                            ? [...currentList, opt.value]
                            : currentList.filter((v) => v !== opt.value);
                          setField('education', next);
                        }}
                        label={opt.label}
                      />
                    );
                  })}
                </div>
              </div>
              )}

              {renderAvitoCheckboxGroup('avitoEducationLevel', 'avitoEducationLevel', 'Образование', AVITO_EDUCATION_OPTIONS)}
              {renderAvitoCheckboxGroup('avitoSchedule', 'avitoSchedule', 'График работы', AVITO_SCHEDULE_OPTIONS)}
              {renderAvitoCheckboxGroup('avitoBusinessTrip', 'avitoBusinessTrip', 'Готовность к командировкам', AVITO_BUSINESS_TRIP_OPTIONS)}

              {/* Тип занятости */}
              {hasSection('employmentType') && (
              <CustomSelect
                label="Тип занятости"
                value={filters.employmentType || ''}
                onChange={(value) => setField('employmentType', value)}
                options={EMPLOYMENT_TYPE_OPTIONS}
                placeholder="Выберите тип занятости..."
              />
              )}
            </div>
          </div>

          {/* Секция "География и профиль" */}
          <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full">
            <div className="font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#868c98] text-[16px] tracking-[-0.176px] w-full">
              <p className="leading-[24px]">География и профиль</p>
            </div>

            <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full">
              {/* География (кнопка-модалка) */}
              <div className="content-stretch flex flex-col gap-[4px] items-center relative shrink-0 w-full">
                <div className="content-stretch flex gap-px items-center relative shrink-0 w-full">
                  <div className="flex flex-col font-['Inter:Regular',_sans-serif] font-normal justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black text-nowrap tracking-[-0.084px]">
                    <p className="leading-[20px] whitespace-pre">География</p>
                  </div>
                </div>
                <button
                  onClick={() => onAreaModalOpen?.()}
                  className="bg-white relative rounded-[10px] shrink-0 w-full hover:bg-[#f6f8fa] transition-colors cursor-pointer"
                >
                  <div className="box-border content-stretch flex gap-[8px] items-center overflow-clip pl-[12px] pr-[10px] py-[10px] relative w-full">
                    <div className="basis-0 font-['Inter:Regular',_sans-serif] font-normal grow leading-[0] min-h-px min-w-px not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-[14px] text-left tracking-[-0.084px]">
                      <p className={`[white-space-collapse:collapse] leading-[20px] overflow-ellipsis overflow-hidden text-left ${(value?.cities?.length || (value as any)?.cityIds?.length) ? 'text-[#525866]' : 'text-[#868c98]'}`}>
                        {geoDisplayCount > 0
                          ? `Выбрано: ${geoDisplayCount}`
                          : (value?.cities?.length ? value.cities.join(', ') : 'Выберите города...')}
                      </p>
                    </div>
                    <div className="overflow-clip relative shrink-0 size-[20px]">
                      <div className="absolute inset-[10%_5%_10%_5%]">
                        <svg className="block max-w-none size-full" fill="none" viewBox="0 0 20 20">
                          <path d="M10.5001 12.4144L15.2072 7.70733L16.2678 8.76794L10.5001 14.5356L4.73242 8.76794L5.79303 7.70733L10.5001 12.4144Z" fill="#525866"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div aria-hidden="true" className="absolute border border-[#e2e4e9] border-solid inset-0 pointer-events-none rounded-[10px] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)]" />
                </button>
              </div>

              {/* Гражданство (кнопка-модалка) */}
              {hasSection('citizenship') && (
              <div className="content-stretch flex flex-col gap-[4px] items-center relative shrink-0 w-full">
                <div className="content-stretch flex gap-px items-center relative shrink-0 w-full">
                  <div className="flex flex-col font-['Inter:Regular',_sans-serif] font-normal justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black text-nowrap tracking-[-0.084px]">
                    <p className="leading-[20px] whitespace-pre">Гражданство</p>
                  </div>
                </div>
                <button
                  onClick={() => onCitizenshipModalOpen?.()}
                  className="bg-white relative rounded-[10px] shrink-0 w-full hover:bg-[#f6f8fa] transition-colors cursor-pointer"
                >
                  <div className="box-border content-stretch flex gap-[8px] items-center overflow-clip pl-[12px] pr-[10px] py-[10px] relative w-full">
                    <div className="basis-0 font-['Inter:Regular',_sans-serif] font-normal grow leading-[0] min-h-px min-w-px not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-[14px] text-left tracking-[-0.084px]">
                      <p className={`[white-space-collapse:collapse] leading-[20px] overflow-ellipsis overflow-hidden text-left ${(Array.isArray(value?.citizenship) && (value?.citizenship?.length ?? 0) > 0) ? 'text-[#525866]' : 'text-[#868c98]'}`}>
                        {Array.isArray(value?.citizenship) && (value?.citizenship?.length ?? 0) > 0
                          ? `Выбрано: ${value?.citizenship?.length}`
                          : 'Выберите гражданство...'}
                      </p>
                    </div>
                    <div className="overflow-clip relative shrink-0 size-[20px]">
                      <div className="absolute inset-[10%_5%_10%_5%]">
                        <svg className="block max-w-none size-full" fill="none" viewBox="0 0 20 20">
                          <path d="M10.5001 12.4144L15.2072 7.70733L16.2678 8.76794L10.5001 14.5356L4.73242 8.76794L5.79303 7.70733L10.5001 12.4144Z" fill="#525866"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div aria-hidden="true" className="absolute border border-[#e2e4e9] border-solid inset-0 pointer-events-none rounded-[10px] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)]" />
                </button>
              </div>
              )}

              {renderAvitoCheckboxGroup('avitoRelocation', 'avitoRelocation', 'Готовность к переезду', AVITO_RELOCATION_OPTIONS)}

              {hasSection('workFormat') && (
              <CustomSelect
                label="Формат работы"
                value={(value as any)?.workFormat || ''}
                onChange={(val) => {
                  if (val !== 'Офис' && val !== 'Гибрид') {
                    onChange?.({ ...(value as any), workFormat: val, metroCityId: '', metro: '' });
                  } else {
                    onChange?.({ ...(value as any), workFormat: val });
                  }
                }}
                options={[
                  { value: '', label: 'Не важно' },
                  { value: 'Офис', label: 'Офис' },
                  { value: 'Гибрид', label: 'Гибрид' },
                  { value: 'Удаленка', label: 'Удаленка' }
                ]}
                placeholder="Выберите формат..."
              />
              )}

              {/* Метро: город + станция */}
              {hasSection('metro') && metroCities.length > 0 && (() => {
                const existingMetroCityId = (value as any)?.metroCityId || '';
                const activeCityForMetro = existingMetroCityId
                  ? metroCities.find((c) => String(c.id) === existingMetroCityId) || null
                  : null;

                return (
                  <>
                    <CustomSelect
                      label="Город (метро)"
                      value={existingMetroCityId}
                      onChange={(val) => onChange?.({ ...(value as any), metroCityId: val, metro: [] })}
                      options={[
                        { value: '', label: 'Выберите город...' },
                        ...metroCities.map((c) => ({ value: String(c.id), label: c.name }))
                      ]}
                      placeholder="Выберите город..."
                    />
                    {activeCityForMetro && (
                      <SearchableMetroSelect
                        value={(value as any)?.metro || ''}
                        onChange={(val) => onChange?.({ ...(value as any), metro: val })}
                        lines={activeCityForMetro.lines}
                        placeholder="Выберите станции..."
                        compact
                        multiple
                        accentColor={colors.primary}
                      />
                    )}
                  </>
                );
              })()}

              {/* Возраст */}
              <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full">
                <div className="flex flex-col font-['Inter:Medium',_sans-serif] font-medium justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black tracking-[-0.084px] w-full">
                  <p className="leading-[20px]">Возраст</p>
                </div>
                <div className="content-stretch flex gap-[8px] items-center relative shrink-0 w-full">
                  <div className="basis-0 content-stretch flex flex-col gap-[4px] grow items-center min-h-px min-w-px relative shrink-0">
                    <div className="bg-white relative rounded-[10px] shrink-0 w-full">
                      <div className="box-border content-stretch flex gap-[8px] items-center overflow-clip pl-[12px] pr-[10px] py-[10px] relative w-full">
                        <input
                          type="number"
                          placeholder="ОТ"
                          value={value?.ageFrom || ''}
                          onChange={(e) => onChange?.({ ...value, ageFrom: e.target.value })}
                          className="basis-0 font-['Inter:Regular',_sans-serif] font-normal grow leading-[0] min-h-px min-w-px not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-[#525866] text-[14px] text-left tracking-[-0.084px] outline-none bg-transparent placeholder:text-[#868c98]"
                        />
                      </div>
                      <div aria-hidden="true" className="absolute border border-[#e2e4e9] border-solid inset-0 pointer-events-none rounded-[10px] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)]" />
                    </div>
                  </div>
                  <div className="basis-0 content-stretch flex flex-col gap-[4px] grow items-center min-h-px min-w-px relative shrink-0">
                    <div className="bg-white relative rounded-[10px] shrink-0 w-full">
                      <div className="box-border content-stretch flex gap-[8px] items-center overflow-clip pl-[12px] pr-[10px] py-[10px] relative w-full">
                        <input
                          type="number"
                          placeholder="ДО"
                          value={value?.ageTo || ''}
                          onChange={(e) => onChange?.({ ...value, ageTo: e.target.value })}
                          className="basis-0 font-['Inter:Regular',_sans-serif] font-normal grow leading-[0] min-h-px min-w-px not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-[#525866] text-[14px] text-left tracking-[-0.084px] outline-none bg-transparent placeholder:text-[#868c98]"
                        />
                      </div>
                      <div aria-hidden="true" className="absolute border border-[#e2e4e9] border-solid inset-0 pointer-events-none rounded-[10px] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)]" />
                    </div>
                  </div>
                </div>
                {hasSection('includeNoAge') && (
                <div className="content-stretch flex gap-[8px] items-center relative shrink-0 w-full">
                  <CustomCheckbox
                    checked={value?.includeNoAge || false}
                    onChange={(checked) => onChange?.({ ...value, includeNoAge: checked })}
                    label="Включая без возраста"
                  />
                </div>
                )}
              </div>


              {/* Пол */}
              <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full">
                <div className="flex flex-col font-['Inter:Medium',_sans-serif] font-medium justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black tracking-[-0.084px] w-full">
                  <p className="leading-[20px]">Пол</p>
                </div>
                <div className="content-stretch flex gap-[20px] items-center relative shrink-0 w-full">
                  <CustomRadio
                    checked={!filters.gender}
                    onChange={() => setField('gender', '')}
                    label="Любой"
                  />
                  <CustomRadio
                    checked={filters.gender === 'M'}
                    onChange={() => setField('gender', 'M')}
                    label="М"
                  />
                  <CustomRadio
                    checked={filters.gender === 'F'}
                    onChange={() => setField('gender', 'F')}
                    label="Ж"
                  />
                </div>
              </div>

              {hasSection('avitoDriverLicence') && (
                <CustomSelect
                  label="Водительское удостоверение"
                  value={filters.avitoDriverLicence || ''}
                  onChange={(v) => setField('avitoDriverLicence', v)}
                  options={[
                    { value: '', label: 'Не важно' },
                    { value: 'yes', label: 'Есть' },
                    { value: 'no', label: 'Нет' },
                  ]}
                  placeholder="Не важно"
                />
              )}
              {renderAvitoDriverCategories()}
              {renderAvitoCheckboxGroup('avitoDrivingExperience', 'avitoDrivingExperience', 'Стаж вождения', AVITO_DRIVING_EXPERIENCE_OPTIONS)}
              {renderAvitoCheckboxGroup('avitoOwnTransport', 'avitoOwnTransport', 'Свой транспорт', AVITO_OWN_TRANSPORT_OPTIONS)}
              {hasSection('avitoMedicalBook') && (
                <CustomSelect
                  label="Медкнижка"
                  value={filters.avitoMedicalBook || ''}
                  onChange={(v) => setField('avitoMedicalBook', v)}
                  options={[
                    { value: '', label: 'Не важно' },
                    { value: 'yes', label: 'Есть' },
                    { value: 'no', label: 'Нет' },
                  ]}
                  placeholder="Не важно"
                />
              )}

              {/* Есть фото — у Avito такого фильтра в API нет (см. openapi.yaml
                  /job/v1/resumes/), поэтому скрываем для источников где не
                  поддерживается. */}
              {hasSection('hasPhoto') && (
                <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full">
                  <CustomCheckbox
                    checked={!!filters.hasPhoto}
                    onChange={(checked) => setField('hasPhoto', checked)}
                    label="Есть фото"
                  />
                </div>
              )}

            </div>
          </div>

          {/* Секция "Компании" */}
          {hasSection('companies') && (
          <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full" data-section="companies">
            <div className="font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#868c98] text-[16px] tracking-[-0.176px] w-full">
              <p className="leading-[24px]">Компании</p>
            </div>

            <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full">
              {/* Стоп-лист компаний */}
              <div className="content-stretch flex flex-col gap-[4px] items-center relative shrink-0 w-full">
                <div className="content-stretch flex gap-px items-center relative shrink-0 w-full">
                  <div className="flex flex-col font-['Inter:Regular',_sans-serif] font-normal justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black text-nowrap tracking-[-0.084px]">
                    <p className="leading-[20px] whitespace-pre">Стоп-лист компаний</p>
                  </div>
                </div>
                <div className="bg-white relative rounded-[10px] shrink-0 w-full">
                  <div className="box-border content-stretch flex gap-[8px] items-center overflow-clip pl-[12px] pr-[10px] py-[10px] relative w-full">
                    <div className="basis-0 font-['Inter:Regular',_sans-serif] font-normal grow leading-[0] min-h-px min-w-px not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-[14px] text-left tracking-[-0.084px]">
                      <div className="flex flex-wrap gap-2 items-center min-h-[20px]">
                        {(value?.stopCompanies || []).map((company, index) => (
                          <span key={index} className="inline-flex items-center rounded-full text-[12px] px-2 py-1 whitespace-nowrap" style={{ backgroundColor: colors.primary + '20', color: colors.primary }}>
                            {company}
                            <button
                              type="button"
                              onClick={() => onChange?.({ ...value, stopCompanies: (value?.stopCompanies || []).filter(c => c !== company) })}
                              className="ml-1 rounded-full p-0.5"
                              style={{ color: colors.primary }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.primary + '40'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                              <XMarkIconSolid className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                        <input
                          type="text"
                          placeholder={(value?.stopCompanies || []).length === 0 ? "Введите название и нажмите Enter" : ""}
                          className="flex-1 min-w-0 outline-none bg-transparent text-[14px] leading-[20px] text-[#525866] placeholder:text-[#868c98]"
                          onChange={(e) => {
                            const inputValue = e.currentTarget.value;
                            if (inputValue.includes(',') || inputValue.includes(';')) {
                              const parts = inputValue.split(/[,;]/);
                              const newItemsRaw = parts.slice(0, -1).map(s => s.trim()).filter(Boolean);
                              const remainingInput = parts.slice(-1)[0];

                              if (newItemsRaw.length > 0) {
                                const existing = value?.stopCompanies || [];
                                const currentSet = new Set(existing);
                                const uniqueNew: string[] = [];
                                for (const item of newItemsRaw) {
                                  if (!currentSet.has(item)) {
                                    uniqueNew.push(item);
                                    currentSet.add(item);
                                  }
                                }
                                if (uniqueNew.length > 0) {
                                  onChange?.({ ...value, stopCompanies: [...existing, ...uniqueNew] });
                                }
                              }
                              e.currentTarget.value = remainingInput;
                            }
                          }}
                          onBlur={(e) => {
                            const company = e.currentTarget.value.trim();
                            if (company) {
                              const existing = value?.stopCompanies || [];
                              if (!existing.includes(company)) {
                                onChange?.({ ...value, stopCompanies: [...existing, company] });
                              }
                              e.currentTarget.value = '';
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const company = e.currentTarget.value.trim();
                              if (company && !(value?.stopCompanies || []).includes(company)) {
                                onChange?.({ ...value, stopCompanies: [...(value?.stopCompanies || []), company] });
                                e.currentTarget.value = '';
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div aria-hidden="true" className="absolute border border-[#e2e4e9] border-solid inset-0 pointer-events-none rounded-[10px] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)]" />
                </div>
                <div className="box-border content-stretch flex gap-[4px] items-start px-0 py-[2px] relative shrink-0 w-full">
                  <div className="basis-0 flex flex-col font-['Inter:Regular',_sans-serif] font-normal grow justify-center leading-[0] min-h-px min-w-px not-italic relative shrink-0 text-[#525866] text-[12px]">
                    <p className="leading-[16px]">Введите название и нажмите Enter</p>
                  </div>
                </div>
              </div>

              {/* Компании-доноры */}
              <div className="content-stretch flex flex-col gap-[4px] items-center relative shrink-0 w-full">
                <div className="content-stretch flex gap-px items-center relative shrink-0 w-full">
                  <div className="flex flex-col font-['Inter:Regular',_sans-serif] font-normal justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black text-nowrap tracking-[-0.084px]">
                    <p className="leading-[20px] whitespace-pre">Компании-доноры</p>
                  </div>
                </div>
                <DonorCompaniesAutocomplete
                  value={value?.donorCompanies || []}
                  onChange={(companies) => onChange?.({ ...value, donorCompanies: companies })}
                  source={filters.source}
                  primaryColor={colors.primary}
                  placeholder="Начните вводить название"
                />
                <div className="box-border content-stretch flex gap-[4px] items-start px-0 py-[2px] relative shrink-0 w-full">
                  <div className="basis-0 flex flex-col font-['Inter:Regular',_sans-serif] font-normal grow justify-center leading-[0] min-h-px min-w-px not-italic relative shrink-0 text-[#525866] text-[12px]">
                    <p className="leading-[16px]">Выберите из подсказок или введите вручную</p>
                  </div>
                </div>
              </div>

              {/* Режим фильтрации компаний */}
              <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-[152px]">
                <div className="content-stretch flex gap-[8px] items-center relative shrink-0">
                  <CustomRadio
                    checked={value?.companyFilterMode === 'current'}
                    onChange={() => onChange?.({ ...value, companyFilterMode: 'current' })}
                    label="Текущая компания"
                  />
                </div>
                <div className="content-stretch flex gap-[8px] items-center relative shrink-0 w-full">
                  <CustomRadio
                    checked={value?.companyFilterMode === 'all_time'}
                    onChange={() => onChange?.({ ...value, companyFilterMode: 'all_time' })}
                    label="За все время работы"
                  />
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Секция "Статус поиска" */}
          {hasSection('jobSearchStatus') && (
            <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full">
              <div className="font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#868c98] text-[16px] tracking-[-0.176px] w-full">
                <p className="leading-[24px]">Статус поиска</p>
              </div>

              <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full">
                {/* Статус поиска */}
                <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full">
                  <div className="flex flex-col font-['Inter:Medium',_sans-serif] font-medium justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black tracking-[-0.084px] w-full">
                    <p className="leading-[20px]">Статус поиска</p>
                  </div>
                  <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full">
                    {[
                      { value: 'active_search', label: 'Активно ищет работу' },
                      { value: 'looking_for_offers', label: 'Рассматривает предложения' },
                      { value: 'has_job_offer', label: 'Предложили работу, решает' },
                      { value: 'accepted_job_offer', label: 'Вышел на новое место' },
                      { value: 'not_looking_for_job', label: 'Не ищет работу' }
                    ].map(status => (
                      <CustomCheckbox
                        key={status.value}
                        checked={filters.jobSearchStatus?.includes(status.value) || false}
                        onChange={(checked) => {
                          const current = filters.jobSearchStatus || [];
                          if (checked) {
                            setField('jobSearchStatus', [...current, status.value]);
                          } else {
                            setField('jobSearchStatus', current.filter((v: string) => v !== status.value));
                          }
                        }}
                        label={status.label}
                      />
                    ))}
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* Секция "Даты" */}
          {(hasSection('dates') || hasSection('avitoUpdatedDate')) && (
          <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full">
            <div className="font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#868c98] text-[16px] tracking-[-0.176px] w-full">
              <p className="leading-[24px]">Даты</p>
            </div>
            <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full">
              {hasSection('avitoUpdatedDate') && (
                <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full">
                  <div className="flex flex-col font-['Inter:Regular',_sans-serif] font-normal justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black tracking-[-0.084px] w-full">
                    <p className="leading-[20px]">Дата обновления резюме</p>
                  </div>
                  <div className="content-stretch flex gap-[8px] items-center relative shrink-0 w-full">
                    <input
                      type="date"
                      value={filters.avitoUpdatedFrom || ''}
                      onChange={(e) => setField('avitoUpdatedFrom', e.target.value)}
                      className="bg-white rounded-[10px] border border-[#e2e4e9] basis-0 grow px-[12px] py-[10px] text-[14px] text-[#525866] outline-none"
                    />
                    <input
                      type="date"
                      value={filters.avitoUpdatedTo || ''}
                      onChange={(e) => setField('avitoUpdatedTo', e.target.value)}
                      className="bg-white rounded-[10px] border border-[#e2e4e9] basis-0 grow px-[12px] py-[10px] text-[14px] text-[#525866] outline-none"
                    />
                  </div>
                </div>
              )}
              {hasSection('dates') && (
                <>
                  <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full">
                    <div className="flex flex-col font-['Inter:Regular',_sans-serif] font-normal justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black tracking-[-0.084px] w-full">
                      <p className="leading-[20px]">{filters.source === 'estaff' ? 'Обновлено' : 'Обновлено после'}</p>
                    </div>
                    {filters.source === 'estaff' ? (
                      <div className="content-stretch flex gap-[8px] items-center relative shrink-0 w-full">
                        <input
                          aria-label="Обновлено с"
                          type="date"
                          value={filters.updatedAfter || ''}
                          onChange={(e) => setField('updatedAfter', e.target.value)}
                          className="bg-white rounded-[10px] border border-[#e2e4e9] basis-0 grow px-[12px] py-[10px] text-[14px] text-[#525866] outline-none"
                        />
                        <input
                          aria-label="Обновлено по"
                          type="date"
                          value={filters.updatedBefore || ''}
                          onChange={(e) => setField('updatedBefore', e.target.value)}
                          className="bg-white rounded-[10px] border border-[#e2e4e9] basis-0 grow px-[12px] py-[10px] text-[14px] text-[#525866] outline-none"
                        />
                      </div>
                    ) : (
                      <input
                        aria-label="Обновлено после"
                        type="date"
                        value={filters.updatedAfter || ''}
                        onChange={(e) => setField('updatedAfter', e.target.value)}
                        className="bg-white rounded-[10px] border border-[#e2e4e9] px-[12px] py-[10px] text-[14px] text-[#525866] outline-none w-full"
                      />
                    )}
                  </div>
                  <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full">
                    <div className="flex flex-col font-['Inter:Regular',_sans-serif] font-normal justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black tracking-[-0.084px] w-full">
                      <p className="leading-[20px]">{filters.source === 'estaff' ? 'Создано' : 'Создано после'}</p>
                    </div>
                    {filters.source === 'estaff' ? (
                      <div className="content-stretch flex gap-[8px] items-center relative shrink-0 w-full">
                        <input
                          aria-label="Создано с"
                          type="date"
                          value={filters.createdAfter || ''}
                          onChange={(e) => setField('createdAfter', e.target.value)}
                          className="bg-white rounded-[10px] border border-[#e2e4e9] basis-0 grow px-[12px] py-[10px] text-[14px] text-[#525866] outline-none"
                        />
                        <input
                          aria-label="Создано по"
                          type="date"
                          value={filters.createdBefore || ''}
                          onChange={(e) => setField('createdBefore', e.target.value)}
                          className="bg-white rounded-[10px] border border-[#e2e4e9] basis-0 grow px-[12px] py-[10px] text-[14px] text-[#525866] outline-none"
                        />
                      </div>
                    ) : (
                      <input
                        aria-label="Создано после"
                        type="date"
                        value={filters.createdAfter || ''}
                        onChange={(e) => setField('createdAfter', e.target.value)}
                        className="bg-white rounded-[10px] border border-[#e2e4e9] px-[12px] py-[10px] text-[14px] text-[#525866] outline-none w-full"
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          )}

        </div>

        </div>
      </div>

      {/* Fixed Search Button at Bottom */}
      <div className="bg-[#f5f6f1] border-t border-[#e2e4e9] py-[12px] px-[12px] w-full">
        <button
          onClick={onSearch}
          className="box-border content-stretch flex gap-[4px] items-center justify-center overflow-clip px-[12px] py-[8px] relative rounded-[12px] shrink-0 w-full transition-colors"
          style={{ backgroundColor: colors.primary }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.primaryHover; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.primary; }}
        >
          <div className="overflow-clip relative shrink-0 size-[24px]">
            <div className="absolute inset-[12.5%_11.32%_11.32%_12.5%]">
              <svg className="block max-w-none size-full" fill="none" viewBox="0 0 24 24">
                <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          <div className="box-border content-stretch flex gap-[8px] items-center justify-center px-[4px] py-0 relative shrink-0">
            <div className="css-hwbbqp font-['Inter_Display:Medium',_sans-serif] leading-[0] not-italic relative shrink-0 text-[20px] text-center text-nowrap text-white">
              <p className="leading-[28px] whitespace-pre">Поиск</p>
            </div>
          </div>
        </button>
      </div>
      </div>

      {/* Модальные окна теперь рендерятся в FiltersModal */}
    </>
  );
};

export default Filters;
